import { forwardRef, type InputHTMLAttributes, type TextareaHTMLAttributes } from 'react';

import { cn } from '../lib/cn';

const control = [
  'w-full rounded-md border bg-surface px-3 text-base text-text',
  'border-border placeholder:text-text-subtle',
  'transition-colors duration-[--hs-duration-fast]',
  'hover:border-border-strong',
  'disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:opacity-60',
  'aria-[invalid=true]:border-danger',
].join(' ');

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> {
  className?: string;
}

/**
 * A text input.
 *
 * `dir="auto"` is the default and matters more than it looks: an Arabic name
 * typed into an English-language form renders left-aligned and mangled without
 * it, and an English URL typed into an Arabic form does the same in reverse.
 * The browser decides per value, which is the only thing that can be right.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, type = 'text', dir = 'auto', ...props },
  ref,
) {
  return (
    <input ref={ref} type={type} dir={dir} className={cn(control, 'h-11', className)} {...props} />
  );
});

export interface TextareaProps extends Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  'className'
> {
  className?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, rows = 4, dir = 'auto', ...props },
  ref,
) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      dir={dir}
      className={cn(control, 'py-2.5 resize-y', className)}
      {...props}
    />
  );
});
